import React from 'react';

import { clsx, colorSet } from '@components/Provider';
import { makeStyles, Theme } from '@material-ui/core';
import { CapaReviewPointRatio } from '@generated/graphql';

interface Props {
  ratio?: number;
  label: keyof Omit<CapaReviewPointRatio, '__typename'>;
  count?: number;
}

const keyToNumber = {
  five: '5',
  four: '4',
  three: '3',
  two: '2',
  one: '1'
};

const RatingRatio = ({ ratio = 0, label, count = 0 }: Props) => {
  const classes = useStyles({ ratio });

  return (
    <div
      className={clsx(classes.root, {
        first: label === 'five'
      })}
    >
      {label === 'five' ? (
        <div className={classes.chip}>
          <span>{count}</span>
        </div>
      ) : null}
      <div className={classes.ratioRoot}>
        {ratio > 0 ? (
          <div className={classes.filled} />
        ) : (
          <div
            className={clsx(classes.rectangle, {
              primary: label === 'five'
            })}
          />
        )}
      </div>
      <span className={classes.score}>{keyToNumber[label]}점</span>
    </div>
  );
};

export default RatingRatio;

const useStyles = makeStyles<Theme, { ratio: number }>(
  (theme) => ({
    root: {
      height: 96,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexDirection: 'column'
    },
    chip: {
      top: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      padding: '0px 4px 1px',
      height: 19,
      borderRadius: 4,
      backgroundColor: colorSet.primary500,
      marginBottom: 5,
      '& span': {
        ...theme.typography.caption,
        color: colorSet.gray000,
        fontWeight: 500,
        whiteSpace: 'pre'
      },
      '&::before': {
        content: '""',
        display: 'block',
        backgroundColor: colorSet.primary500,
        width: 3,
        height: 3,
        bottom: -2,
        position: 'absolute',
        left: 'calc(50% - 2px)',
        transform: 'rotate(-45deg)'
      }
    },
    ratioRoot: {
      width: 8,
      height: 52,
      backgroundColor: colorSet.gray300,
      position: 'relative',
      borderRadius: '8px',
      marginBottom: 2
    },
    rectangle: {
      zIndex: 1,
      borderRadius: '50%',
      width: 8,
      height: 8,
      backgroundColor: colorSet.gray500,
      position: 'absolute',
      bottom: 0,
      '&.primary': {
        backgroundColor: colorSet.primary500
      }
    },
    filled: {
      width: 8,
      maxHeight: 52,
      height: ({ ratio }) => (ratio ? `${ratio}%` : 0),
      backgroundColor: colorSet.primary500,
      position: 'absolute',
      borderRadius: '8px',
      zIndex: 1,
      bottom: 0
    },
    score: {
      ...theme.typography.caption,
      fontWeight: 'normal',
      color: colorSet.gray900
    }
  }),
  {
    classNamePrefix: 'rating__ratio'
  }
);
